import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '../auth.service';
import { ForgotPasswordDialogComponent } from '../forgot-password-dialog/forgot-password-dialog.component';
import { ErrorDialogComponent } from '../error-dialog/error-dialog.component';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatSnackBarModule,
    MatIconModule
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent {
  loginForm: FormGroup;
  loading = false;
  error: string | null = null;
  successMessage: string | null = null;
  hidePassword = true;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]]
    });

    // Check for message parameter in URL (for password reset success)
    this.route.queryParams.subscribe(params => {
      if (params['message']) {
        this.snackBar.open(params['message'], 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['success-snackbar']
        });
      }
    });
  }

  onSubmit() {
    if (this.loginForm.valid) {
      this.loading = true;
      this.error = null;
      
      const { email, password } = this.loginForm.value;
      
      this.authService.signIn(email, password).subscribe({
        next: () => {
          // Navigation is handled in the AuthService
        },
        error: (err) => {
          console.error('Login error:', err);
          this.error = err.message || 'Invalid email or password';
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        }
      });
    }
  }

  forgotPassword() {
    const emailControl = this.loginForm.get('email');
    
    if (emailControl?.valid) {
      const email = emailControl.value;
      
      // Open confirmation dialog
      const dialogRef = this.dialog.open(ForgotPasswordDialogComponent, {
        width: '400px',
        data: { email }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          // User confirmed, send reset email
          const dialogInstance = dialogRef.componentInstance;
          dialogInstance.setLoading(true);
          
          this.authService.requestPasswordReset(email).subscribe({
            next: (message) => {
              // Close the dialog
              dialogRef.close();
              
              // Show success message in snackbar
              this.snackBar.open(message, 'Close', {
                duration: 5000,
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                panelClass: ['success-snackbar']
              });
            },
            error: (err) => {
              console.error('Password reset request error:', err);
              // Close the dialog
              dialogRef.close();
              
              // Show error message in snackbar
              this.snackBar.open(err.message, 'Close', {
                duration: 5000,
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                panelClass: ['error-snackbar']
              });
            }
          });
        }
      });
    } else {
      // Show error dialog for invalid email
      this.dialog.open(ErrorDialogComponent, {
        width: '350px',
        data: { message: 'Please enter a valid email address first.' }
      });
      
      // Mark email field as touched to show validation errors
      emailControl?.markAsTouched();
      // Remove the error message from the login form since we're showing it in a dialog
      this.error = null;
    }
  }

  togglePasswordVisibility(): void {
    this.hidePassword = !this.hidePassword;
  }
}
